import React, { FC, useEffect, useReducer } from 'react';

import { ManualColumnClassificationCreate } from '@ariksa/data-scanning/api';
import { Text } from '@chakra-ui/react';
import { map } from 'lodash';
import * as yup from 'yup';

import { DataScanningService } from 'api/services';
import { Form } from 'components/DataEntry';
import {
  ExclamationTriangleIcon,
  IconTypes,
  InformationAlert,
  PageHeaderWithIcon,
} from 'components/index';
import { Modal } from 'components/Overlay';
import { useToasts } from 'components/Toast';

export interface ManualColumnClassificationTarget {
  databaseName: string;
  schemaName: string;
  tableName: string;
  columnName?: string;
}

export const getSnowflakeTableTarget = (
  resource: Record<string, any> = {},
  insight: Record<string, any> = {},
): ManualColumnClassificationTarget => ({
  databaseName:
    resource?.config?.db_name ??
    resource?.config?.database_name ??
    insight?.config?.db_name ??
    insight?.config?.database_name ??
    '',
  schemaName:
    resource?.config?.schema_name ?? insight?.config?.schema_name ?? '',
  tableName:
    insight?.name ?? resource?.config?.name ?? resource?.general?.name ?? '',
});

export const parseDatabaseObjectPath = (path = ''): string[] => {
  const parts = path.match(/"(?:[^"]|"")*"|[^/.]+/g) ?? [];
  return parts.map(part =>
    part.startsWith('"') && part.endsWith('"')
      ? part.slice(1, -1).replace(/""/g, '"')
      : part,
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  target: ManualColumnClassificationTarget;
  mode: 'add' | 'replace';
  initialDataType?: string;
  onSaved: () => void;
}

interface DataTypeOption {
  label: string;
  value: string;
}

interface ManualColumnClassificationState {
  columnName: string;
  selectedDataType: DataTypeOption | null;
  dataTypeOptions: DataTypeOption[];
  isLoadingDataTypes: boolean;
}

const formValidation = yup.object().shape({
  database: yup.string().trim().required('Enter a database name.'),
  schema: yup.string().trim().required('Enter a schema name.'),
  table: yup.string().trim().required('Enter a table name.'),
  column: yup.string().trim().required('Enter a column name.'),
  dataType: yup
    .object()
    .test('required', 'Select a data type.', value => !!value?.value),
});

export const ManualColumnClassificationModal: FC<Props> = ({
  isOpen,
  onClose,
  accountId,
  target,
  mode,
  initialDataType,
  onSaved,
}) => {
  const { errorToast, successToast } = useToasts();

  const [state, updateState] = useReducer(
    (
      prev: ManualColumnClassificationState,
      next: Partial<ManualColumnClassificationState>,
    ) => {
      return { ...prev, ...next };
    },
    {
      columnName: '',
      selectedDataType: null,
      dataTypeOptions: [],
      isLoadingDataTypes: false,
    },
  );

  useEffect(() => {
    if (!isOpen) return;

    updateState({
      columnName: target.columnName ?? '',
      selectedDataType: null,
    });
  }, [
    isOpen,
    target.databaseName,
    target.schemaName,
    target.tableName,
    target.columnName,
    initialDataType,
  ]);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;
    updateState({ isLoadingDataTypes: true });
    DataScanningService.DataTypes.getDataTypes()
      .then(response => {
        if (mounted) {
          let options = map(response.data, o => ({
            label: o.name,
            value: o.name,
          }));
          updateState({ dataTypeOptions: options });
        }
      })
      .catch(() => {
        if (mounted) {
          errorToast({ title: 'Unable to load data types.' });
        }
      })
      .finally(() => {
        if (mounted) {
          updateState({ isLoadingDataTypes: false });
        }
      });

    return () => {
      mounted = false;
    };
  }, [errorToast, isOpen]);

  useEffect(() => {
    if (isOpen && !state.selectedDataType && state.dataTypeOptions.length > 0) {
      const initialOption = initialDataType
        ? state.dataTypeOptions.find(option => option.value === initialDataType)
        : undefined;
      updateState({
        selectedDataType: initialOption ?? state.dataTypeOptions[0],
      });
    }
  }, [initialDataType, isOpen, state.dataTypeOptions, state.selectedDataType]);

  const handleSubmit = async () => {
    try {
      const classification: ManualColumnClassificationCreate = {
        service: 'SnowflakeTables',
        database_name: target.databaseName.trim(),
        schema_name: target.schemaName.trim(),
        table_name: target.tableName.trim(),
        column_name: state.columnName.trim(),
        data_types: [state.selectedDataType?.value ?? ''],
        mode,
      };
      await DataScanningService.DataTypes.addManualColumnClassification({
        accountId,
        manualColumnClassificationCreate: classification,
      });
      successToast({
        title: mode === 'add' ? 'Classification added.' : 'Data type updated.',
      });
      onSaved();
      onClose();
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      errorToast({
        title: 'Unable to save column classification.',
        description:
          typeof detail === 'string'
            ? detail
            : 'Please check the column details and try again.',
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <PageHeaderWithIcon
          label={mode === 'add' ? 'Add classification' : 'Modify data type'}
          iconType={IconTypes.DataClassification}
        />
      }
      styles={{ modal: { size: 'lg' } }}
      body={
        <Form
          schema={{
            database: {
              type: 'display',
              label: 'Database',
              isRequired: true,
              value: target.databaseName,
            },
            schema: {
              type: 'display',
              label: 'Schema',
              isRequired: true,
              value: target.schemaName,
            },
            table: {
              type: 'display',
              label: 'Table',
              isRequired: true,
              value: target.tableName,
            },
            column:
              mode === 'replace'
                ? {
                    type: 'display',
                    label: 'Column',
                    isRequired: true,
                    value: target.columnName,
                  }
                : {
                    type: 'text',
                    label: 'Column',
                    isRequired: true,
                    value: state.columnName,
                    onChange: value => updateState({ columnName: value }),
                  },
            dataType: {
              type: 'react-select',
              label: 'Data type',
              isRequired: true,
              options: state.dataTypeOptions,
              value: state.selectedDataType,
              onChange: option => updateState({ selectedDataType: option }),
              isLoading: state.isLoadingDataTypes,
              placeholder: 'Select data type(s)',
              showIconInValueContainer: false,
            },
          }}
          formValidation={formValidation}
          buttonOptions={{
            submit: {
              name: mode === 'add' ? 'ADD CLASSIFICATION' : 'UPDATE DATA TYPE',
            },
            reset: {
              isVisible: true,
              name: 'CANCEL',
              onClick: onClose,
            },
          }}
          handleSubmit={handleSubmit}
        >
          <InformationAlert
            description="This updates the column classification stored in Ariksa. It does not
          change tags in Snowflake."
            icon={<ExclamationTriangleIcon color="red" />}
          />
        </Form>
      }
    />
  );
};
